/**
 * Settings' Business Details: the EIN, legal name, trade name and address the
 * IRS knows the business by (issue #44, the operator's "Option 1 store it",
 * 2026-10-08). The filled Form 941's header and the W-2 worksheet's boxes b
 * and c read them, so they are entered once, here.
 *
 * THE FORM SHOWS WHAT THE SERVER HOLDS AFTER A SAVE, not what was typed: the
 * server writes an EIN typed without its dash as 12-3456789 and a state as
 * TX, and the answer to a save replaces the fields, so the screen never
 * claims a shape the return will not carry. A refusal names the field in a
 * sentence and leaves what was typed in place to correct.
 *
 * Read on its own, as the Payroll section's setting is, so a failure here
 * leaves the rest of Settings working and is said in this section alone.
 *
 * THE BUSINESS TYPE (migrations 0078 and 0094) is shown here, as a sentence:
 * what kind of business the books are kept for. Its tax facts are fixed once
 * anything is posted, so nothing here changes them. Its legal form moves no
 * number and may change, within what its tax status allows (the plan's §1.3):
 * an LLC still recorded without its members is asked them, and leaves the bare
 * LLC for good once it answers (round 181's TX-4).
 */

import { useEffect, useState } from 'react';
import { ApiError, api, type BusinessDetails, type BusinessType } from '../api.js';
import { InfoNote } from './InfoNote.js';
import { Toast } from './Toast.js';
import { LEGAL_FORMS, LEGAL_FORM_NAMES, offeredTaxStatuses, type ChosenLegalForm, type LegalForm } from '../lib/business-types.js';

type Form = Record<keyof BusinessDetails, string>;

const EMPTY: Form = {
  ein: '',
  legalName: '',
  tradeName: '',
  addressStreet: '',
  addressCity: '',
  addressState: '',
  addressZip: '',
};

const toForm = (details: BusinessDetails): Form =>
  Object.fromEntries(Object.entries(details).map(([key, value]) => [key, value ?? ''])) as Form;

const FOUNDATION: Record<NonNullable<BusinessType['foundationStatus']>, string> = {
  public_charity: 'a public charity',
  private_operating_foundation: 'a private operating foundation',
  private_nonoperating_foundation: 'a private non-operating foundation',
};

const RETURN: Record<NonNullable<BusinessType['annualReturn']>, string> = {
  '990_n': 'Form 990-N',
  '990_ez': 'Form 990-EZ',
  '990': 'Form 990',
  '990_pf': 'Form 990-PF',
};

/** "A single-member LLC", as the sentence below begins it. */
function aForm(form: LegalForm): string {
  if (form === 'llc') return 'An LLC';
  const name = LEGAL_FORM_NAMES[form].toLowerCase().replace('llc', 'LLC');
  return `A ${name}`;
}

/** "A non-profit corporation exempt under 501(c)(3): a private operating foundation, filing Form 990-PF." */
export function describeBusinessType(type: BusinessType): string {
  if (type.taxStatus === 's_corporation') {
    return `${aForm(type.legalForm)} taxed as an S corporation, filing Form 1120-S each year.`;
  }
  if (type.taxStatus === 'partnership') {
    return `${aForm(type.legalForm)} taxed as a partnership, filing Form 1065 each year.`;
  }
  if (type.taxStatus === 'c_corporation') {
    return `${aForm(type.legalForm)} taxed as a C corporation, filing Form 1120 each year.`;
  }
  if (type.taxStatus === 'disregarded_entity') {
    return `${aForm(type.legalForm)} taxed with its owner: its income goes on Schedule C of the owner's Form 1040.`;
  }
  return (
    `${aForm(type.legalForm)} exempt under 501(c)(3): ${type.foundationStatus ? FOUNDATION[type.foundationStatus] : 'its status unrecorded'}, ` +
    `filing ${type.annualReturn ? RETURN[type.annualReturn] : 'its annual return'} each year.`
  );
}

/** The legal forms a business may move to: those its tax status allows, and the bare LLC only while it has it. */
function legalFormsFor(type: BusinessType): LegalForm[] {
  const forms: LegalForm[] = LEGAL_FORMS.filter((form) => offeredTaxStatuses(form).includes(type.taxStatus));
  return type.legalForm === 'llc' ? ['llc', ...forms] : forms;
}

const toDetails = (form: Form): BusinessDetails =>
  Object.fromEntries(Object.entries(form).map(([key, value]) => [key, value.trim() === '' ? null : value])) as unknown as BusinessDetails;

export function BusinessDetailsSection() {
  const [form, setForm] = useState<Form | null>(null);
  // Undefined until read; null when the business's type was never recorded.
  const [type, setType] = useState<BusinessType | null | undefined>(undefined);
  const [legalForm, setLegalForm] = useState<'' | LegalForm>('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .getBusinessDetails()
      .then((result) => {
        if (!live) return;
        setForm(toForm(result.details));
        setType(result.type);
        if (result.type) setLegalForm(result.type.legalForm);
      })
      .catch((caught: unknown) => {
        if (live) setLoadError(caught instanceof ApiError ? caught.message : 'Could not read the business details.');
      });
    return () => {
      live = false;
    };
  }, []);

  async function save() {
    if (!form) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      // The legal form is saved with the rest, by the one Save (0078, 0094). The
      // bare LLC is never sent: it is only ever kept.
      if (type && legalForm !== '' && legalForm !== 'llc' && legalForm !== type.legalForm) {
        setType((await api.setLegalForm(legalForm as ChosenLegalForm)).type);
      }
      const result = await api.setBusinessDetails(toDetails(form));
      setForm(toForm(result.details));
      setNotice('Business details saved');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not save the business details.');
    } finally {
      setBusy(false);
    }
  }

  const field = (key: keyof BusinessDetails, label: string, extra: { className?: string; inputMode?: 'numeric'; maxLength?: number } = {}) => (
    <label className={extra.className ? `field ${extra.className}` : 'field'}>
      <span>{label}</span>
      <input
        value={form?.[key] ?? ''}
        inputMode={extra.inputMode}
        maxLength={extra.maxLength}
        onChange={(event) => {
          const value = event.target.value;
          setForm((current) => ({ ...(current ?? EMPTY), [key]: value }));
        }}
      />
    </label>
  );

  return (
    <section className="settings-section" aria-labelledby="settings-business">
      <h2 id="settings-business">Business Details</h2>
      <p className="muted">
        As the IRS knows the business. The filled Form 941 and the W-2 worksheet carry them.{' '}
        <InfoNote label="What These Fill">
          The filled Form 941&apos;s header, with the EIN and name again at the top of its second page, and each Form
          W-2&apos;s boxes b and c and the Form W-3&apos;s boxes e to g, which the instructions ask to match your Forms
          941. The legal name is the one on the return, not the trade name; while it is blank, the business&apos;s name
          in this application is used. A detail left blank is left blank on the form, for you to fill before signing.
        </InfoNote>
      </p>

      {notice && !error && <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />}
      {loadError && (
        <p role="alert" className="error">
          {loadError}
        </p>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {type !== undefined && (
        <p aria-label="Business Type">
          <strong>Business Type</strong>{' '}
          {type === null
            ? 'Not recorded. Payroll, the year-end reports and the close refuse to run until the operator records it with apps/api/scripts/set-business-type.mjs.'
            : describeBusinessType(type)}
        </p>
      )}
      {type?.legalForm === 'llc' && (
        <p className="muted">
          Say whether the LLC has one member or several. It changes nothing in the books, and once saved the business is
          recorded that way.
        </p>
      )}
      {form === null ? (
        !loadError && <p className="muted">Loading…</p>
      ) : (
        <div className="new-account" role="group" aria-label="Business Details">
          <div className="field-row">
            {type && legalFormsFor(type).length > 1 && (
              // The default width: "Non-Profit Corporation" is cut off at the medium one.
              <label className="field">
                <span>Legal Form</span>
                <select
                  value={legalForm}
                  onChange={(event) => {
                    const next = event.target.value as LegalForm;
                    setLegalForm(next);
                  }}
                >
                  {legalFormsFor(type).map((form) => (
                    <option key={form} value={form}>
                      {LEGAL_FORM_NAMES[form]}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {field('ein', 'EIN', { className: 'field-medium', inputMode: 'numeric', maxLength: 10 })}
            {field('legalName', 'Legal Name')}
            {field('tradeName', 'Trade Name')}
          </div>
          <div className="field-row">
            {field('addressStreet', 'Street Address')}
            {field('addressCity', 'City', { className: 'field-medium' })}
            {field('addressState', 'State', { className: 'field-short', maxLength: 2 })}
            {field('addressZip', 'ZIP Code', { className: 'field-medium', inputMode: 'numeric', maxLength: 10 })}
          </div>
          <button type="button" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save Business Details'}
          </button>
        </div>
      )}
    </section>
  );
}
